import {useEffect, useState} from 'react';
import {
  Button,
  Box,
  Flex,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
  FormControl,
  Text,
  VStack,
  useDisclosure,
  Alert, AlertIcon, AlertTitle, AlertDescription, CloseButton, IconButton, Spinner
} from '@reverb-ui/react';
import { ChevronRight, Eye, EyeOff } from '@reverb-ui/icons-react';
import { Link } from 'components/Link';

import { CHOOSE_CONSOLE, RESET_PASSWORD } from 'router/routes';
import {useForm, useWatch} from 'react-hook-form';
import { useHistory } from 'react-router';
import { useAuth } from 'contexts';

const SsoLogin = () => {
  const history = useHistory();
  const { loading, ssoLogin, token } = useAuth();
  const [show, setShow] = useState(false);
  const handleClick = () => setShow(!show);

  const {
    isOpen: isErrorAlertOpen,
    onOpen: openErrorAlert,
    onClose: closeErrorAlert
  } = useDisclosure()

  const { register, handleSubmit, control } = useForm();

  useEffect(() => {
    if (token) {
      history.push(CHOOSE_CONSOLE)
    }
  }, [token])

  const formValues = useWatch({control})

  const onSubmitSsoLogin = async (data: { login: string, password: string}) => {
    const statusCode = await ssoLogin({
      email: data.login,
      password: data.password
    });

    if (statusCode === 200) {
      history.push(CHOOSE_CONSOLE);
    } else {
      openErrorAlert()
    }
  };

  return (
    <Box
      transition="all 0.5s"
      bg="white"
      flexDirection="column"
      borderRadius="2xl"
      p={{ base: 0, "tablet-md": 12 }}
      w="full"
    >
      <VStack alignItems="initial" spacing={12}>
        <Box width="full">
          <Heading
            as="h1"
            width="full"
            fontFamily="manrope"
            fontSize={{ "base": "2xl", "tablet-md": "5xl" }}
            fontWeight="extrabold"
            >
            Log in with SSO
          </Heading>
        </Box>
        {isErrorAlertOpen && (
          <Alert status="error" alignItems="start" borderRadius={6} w="full">
            <AlertIcon h="15px" mt={2} />
            <Box flex={1}>
              <AlertTitle>Ooops!</AlertTitle>
              <AlertDescription fontSize="sm">
                E-mail and/or password not found.
              </AlertDescription>
            </Box>
            <CloseButton onClick={closeErrorAlert} size="sm" />
          </Alert>
        )}
        <form onSubmit={handleSubmit(onSubmitSsoLogin)}>
          <FormControl>
            <VStack width="full" spacing={4} className='vstack-login'>
              <VStack width="full" alignItems="start" className='user-email'>
                <Text fontSize="md">E-mail</Text>
                <Input
                  placeholder="Enter your e-mail"
                  id="login"
                  aria-label="login"
                  size="md"
                  variant="outline"
                  {...register('login')}
                />
              </VStack>
              <VStack width="full" alignItems="start" className="user-password">
                <Text fontSize="md">Password</Text>
                <InputGroup size="md">
                  <Input
                    placeholder="Enter your password"
                    id="password"
                    pr="4.5rem"
                    type={show ? 'text' : 'password'}
                    {...register('password')}
                  />
                  <InputRightElement width="4.5rem">
                    <Button h="1.75rem" size="sm" onClick={handleClick}>
                      {show ? (
                        <Eye color="gray.500" />
                      ) : (
                        <EyeOff color="gray.500" />
                      )}
                    </Button>
                  </InputRightElement>
                </InputGroup>
              </VStack>
              <Flex
                width="full"
                alignItems="center"
                justifyContent="center"
              >
                {loading ?
                  <IconButton
                    minW="103px"
                    colorScheme="blue"
                    aria-label="Loading"
                    icon={<Spinner color="white"/>}
                  /> :
                  <Button
                    disabled={!formValues.password || !formValues.login}
                    type="submit"
                    p="4"
                    bg="blue.500"
                    size="lg"
                    color="white"
                    colorScheme="blue"
                    rightIcon={<ChevronRight color="white" />}
                  >
                    Log in
                  </Button>
                }
              </Flex>
            </VStack>
          </FormControl>
        </form>
        <VStack>
          <Link path={RESET_PASSWORD}>
            <Text color="blue.500" fontWeight="semibold">
              Forgot your password?
            </Text>
          </Link>
        </VStack>
      </VStack>
    </Box>
  );
};

export default SsoLogin;
